// Utility functions
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Date formatting
export function formatDate(dateString, options = {}) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...options,
  });
}

export function formatDateTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Date helpers
export function getToday() {
  return new Date().toISOString().split('T')[0];
}

export function getMonthRange(year, month) {
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
}

export function getDaysInMonth(year, month) {
  const days = [];
  const lastDay = new Date(year, month + 1, 0).getDate();
  for (let day = 1; day <= lastDay; day++) {
    const date = new Date(year, month, day);
    days.push({
      date: date.toISOString().split('T')[0],
      dayOfWeek: date.getDay(),
      dayName: date.toLocaleDateString('de-DE', { weekday: 'short' }),
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
    });
  }
  return days;
}

// Role labels in German
export const roleLabels = {
  DEVELOPER: 'Developer',
  WACHLEITUNG: 'Wachleitung',
  WACHLEITUNG_VERTRETUNG: 'Wachleitung (Vertretung)',
  WACHABTEILUNGSLEITUNG: 'Wachabteilungsleitung',
  ADMIN: 'Admin',
  MITARBEITER: 'Mitarbeiter',
};

// Dienst art labels
export const dienstArtLabels = {
  T: 'Tag',
  N: 'Nacht',
  B: 'Bereitschaft',
  S: 'Sonder',
};

// Qualifikation labels
export const qualifikationLabels = {
  RS: 'Rettungssanitäter',
  RA: 'Rettungsassistent',
  NotSan: 'Notfallsanitäter',
  RH: 'Rettungshelfer',
  RDV: 'Rettungsdienstverwaltung',
};

// Fahrzeug type colors
export const fahrzeugTypeColors = {
  LHF: 'bg-red-500',
  HLF: 'bg-red-500',
  DLK: 'bg-orange-500',
  RTW: 'bg-green-500',
  NEF: 'bg-blue-500',
  ELW: 'bg-purple-500',
  GW: 'bg-gray-500',
  TLF: 'bg-red-600',
  RW: 'bg-yellow-600',
  KLEF: 'bg-teal-500',
  MTF: 'bg-slate-500',
  SONSTIGE: 'bg-gray-400',
};

// Get initials from name
export function getInitials(vorname, nachname) {
  const v = vorname?.[0]?.toUpperCase() || '';
  const n = nachname?.[0]?.toUpperCase() || '';
  return `${v}${n}`;
}

// Format percentage
export function formatPercent(value, decimals = 1) {
  if (value === null || value === undefined) return '—';
  return `${Number(value).toFixed(decimals)}%`;
}

// Truncate text
export function truncate(str, length = 30) {
  if (!str) return '';
  return str.length > length ? `${str.substring(0, length)}...` : str;
}

// Sort by multiple keys
export function sortBy(array, ...keys) {
  return [...array].sort((a, b) => {
    for (const key of keys) {
      const aVal = a[key];
      const bVal = b[key];
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
    }
    return 0;
  });
}

// Extract error message from API response (handles Pydantic validation errors)
export function getErrorMessage(error, defaultMsg = 'Ein Fehler ist aufgetreten') {
  const detail = error?.response?.data?.detail;
  
  if (!detail) {
    return error?.message || defaultMsg;
  }
  
  // String error
  if (typeof detail === 'string') {
    return detail;
  }
  
  // Array of validation errors (Pydantic)
  if (Array.isArray(detail)) {
    return detail
      .map(e => {
        if (typeof e === 'string') return e;
        if (e.msg) return e.msg;
        if (e.message) return e.message;
        return JSON.stringify(e);
      })
      .join(', ');
  }
  
  // Object error
  if (typeof detail === 'object') {
    if (detail.msg) return detail.msg;
    if (detail.message) return detail.message;
    return JSON.stringify(detail);
  }
  
  return defaultMsg;
}

// Group array by key
export function groupBy(array, key) {
  return array.reduce((result, item) => {
    const group = item[key];
    if (!result[group]) result[group] = [];
    result[group].push(item);
    return result;
  }, {});
}

// Download blob as file
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
